import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Check, Phone, User as UserIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { useToast } from '../ui/Alert';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../api/endpoints/auth';
import { ApiError } from '../../types/api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/**
 * Dial codes offered for the account phone. Same list the storefront's
 * inline-auth popup uses, so a merchant sees identical options wherever they
 * enter a number.
 */
const COUNTRY_CODES: { code: string; name: string }[] = [
  { code: '966', name: 'السعودية' },
  { code: '971', name: 'الإمارات' },
  { code: '965', name: 'الكويت' },
  { code: '973', name: 'البحرين' },
  { code: '968', name: 'عُمان' },
  { code: '974', name: 'قطر' },
  { code: '20', name: 'مصر' },
  { code: '962', name: 'الأردن' },
  { code: '961', name: 'لبنان' },
  { code: '964', name: 'العراق' },
  { code: '970', name: 'فلسطين' },
  { code: '963', name: 'سوريا' },
  { code: '967', name: 'اليمن' },
  { code: '249', name: 'السودان' },
  { code: '216', name: 'تونس' },
  { code: '213', name: 'الجزائر' },
  { code: '212', name: 'المغرب' },
  { code: '218', name: 'ليبيا' },
  { code: '44', name: 'بريطانيا' },
  { code: '1', name: 'أمريكا' },
  { code: '90', name: 'تركيا' },
];

/** Machine-readable `details.reason` values the API attaches to its errors. */
const REASON_KEYS: Record<string, string> = {
  username_taken: 'accountModal.usernameTaken',
  username_invalid: 'accountModal.usernameInvalid',
  nickname_invalid: 'accountModal.nicknameInvalid',
  bio_too_long: 'accountModal.bioTooLong',
  avatar_invalid: 'accountModal.avatarInvalid',
  avatar_too_large: 'accountModal.imageTooLarge',
  phone_invalid: 'accountModal.phoneInvalid',
  phone_same: 'accountModal.phoneSame',
  phone_taken: 'accountModal.phoneTaken',
  otp_cooldown: 'accountModal.otpCooldown',
  otp_send_failed: 'accountModal.otpSendFailed',
  otp_expired: 'accountModal.otpExpired',
  otp_too_many: 'accountModal.otpTooMany',
  otp_wrong: 'accountModal.otpWrong',
};

/**
 * In-place profile editor. Replaces the legacy 00acc /account page redirect.
 *
 * Everything is saved through zero's own same-origin API
 * (/dashboard/auth/profile). It used to POST cross-origin to 00acc's
 * /editprofile, which only worked while the browser still had a session
 * cookie on 00acc.com — a third-party cookie from the dashboard's origin, so
 * Safari/Firefox and any Chrome with third-party cookies off silently failed
 * the save. The server writes the avatar to the same CDN location 00acc used
 * (usersAttachments/, basename in `accounts.profileIcon`), so the legacy
 * profile page keeps rendering the same image.
 *
 * The phone number is edited separately from the Save button: it can only be
 * written after a 4-digit code sent to the NEW number over WhatsApp (Gupshup,
 * with an SMS fallback) comes back verified — the same proof-of-ownership
 * flow the storefront uses for customers.
 *
 * Banner upload is intentionally omitted: the only place a banner would
 * render is this dropdown itself, which isn't worth the field.
 */
export function MyAccountModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();

  const [username, setUsername] = useState('');
  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  // Holds the new avatar (base64 data URL) until save. Drives the <img> src
  // along with the existing CDN URL from /me.
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phone sub-flow: 'idle' shows the current number, 'edit' collects the new
  // one, 'otp' collects the code sent to it.
  const [phoneStep, setPhoneStep] = useState<'idle' | 'edit' | 'otp'>('idle');
  const [phoneCc, setPhoneCc] = useState('966');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Reset to live values whenever the modal opens — picks up edits made
  // elsewhere and discards any unsaved state from a previous open.
  useEffect(() => {
    if (!isOpen) return;
    setUsername(user?.username ?? '');
    setNickname(user?.nickname ?? '');
    setBio(user?.bio ?? '');
    setAvatarDataUrl(null);
    setError(null);
    setPhoneStep('idle');
    setPhoneCc(user?.phoneCountryCode || '966');
    setPhoneNumber('');
    setOtpCode('');
    setPhoneError(null);
  }, [isOpen, user]);

  /** Localize an API failure via its `details.reason`, else show its message. */
  const messageFor = (err: unknown, fallbackKey: string): string => {
    if (err instanceof ApiError) {
      const details = err.details as { reason?: string; retryAfter?: number } | undefined;
      const reason = details?.reason;
      // The cooldown is the one reason carrying a number worth showing.
      if (reason === 'otp_cooldown') {
        return t('accountModal.otpCooldown', { seconds: details?.retryAfter ?? 60 });
      }
      if (reason && REASON_KEYS[reason]) return t(REASON_KEYS[reason]);
      if (err.message) return err.message;
    }
    return t(fallbackKey);
  };

  const readAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    set: (v: string) => void,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setError(t('accountModal.imageTooLarge'));
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      set(dataUrl);
      setError(null);
    } catch {
      setError(t('accountModal.saveFailed'));
    } finally {
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setError(null);

    // Send only what actually changed. Some legacy accounts carry a username
    // that no longer passes validation (underscores, over 24 chars); echoing
    // it back untouched would fail a save the merchant only opened to swap
    // their avatar.
    const patch: { username?: string; nickname?: string; bio?: string; avatar?: string } = {};
    const trimmedUsername = username.replace(/\s/g, '');
    if (trimmedUsername !== (user?.username ?? '')) patch.username = trimmedUsername;
    if (nickname !== (user?.nickname ?? '')) patch.nickname = nickname;
    if (bio !== (user?.bio ?? '')) patch.bio = bio;
    if (avatarDataUrl) patch.avatar = avatarDataUrl;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await authApi.updateProfile(patch);
      // The server already dropped its cached `accounts` doc, so /me comes
      // back fresh and the topbar picks up the new avatar/username.
      await refreshUser();
      toast('success', t('accountModal.savedSuccess'));
      onClose();
    } catch (err) {
      setError(messageFor(err, 'accountModal.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendOtp = async () => {
    setPhoneError(null);
    const digits = phoneNumber.replace(/\D/g, '');
    if (!digits) {
      setPhoneError(t('accountModal.phoneRequired'));
      return;
    }
    setPhoneBusy(true);
    try {
      await authApi.sendPhoneOtp(digits, phoneCc);
      setOtpCode('');
      setPhoneStep('otp');
    } catch (err) {
      setPhoneError(messageFor(err, 'accountModal.otpSendFailed'));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    setPhoneError(null);
    const code = otpCode.replace(/\D/g, '');
    if (code.length !== 4) {
      setPhoneError(t('accountModal.otpRequired'));
      return;
    }
    setPhoneBusy(true);
    try {
      await authApi.verifyPhoneOtp(code);
      await refreshUser();
      toast('success', t('accountModal.phoneSaved'));
      setPhoneStep('idle');
      setPhoneNumber('');
      setOtpCode('');
    } catch (err) {
      setPhoneError(messageFor(err, 'accountModal.saveFailed'));
    } finally {
      setPhoneBusy(false);
    }
  };

  const avatarSrc = avatarDataUrl || user?.profileIcon || '';
  const hasPhone = !!user?.phoneNumber;
  const currentPhone = hasPhone ? `+${user?.phoneCountryCode} ${user?.phoneNumber}` : '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('accountModal.title')} size="md">
      <p className="-mt-1 mb-5 text-sm text-text-muted">{t('accountModal.subtitle')}</p>

      {/* Avatar — click to upload */}
      <div className="mb-5 flex justify-center">
        <button
          type="button"
          onClick={() => avatarInputRef.current?.click()}
          className="group relative h-24 w-24 overflow-hidden rounded-full border border-border-default bg-bg-tertiary"
          aria-label={t('accountModal.uploadAvatar')}
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-text-muted">
              <UserIcon className="h-10 w-10" />
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <Camera className="h-5 w-5 text-white" />
          </div>
        </button>
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e, setAvatarDataUrl)}
        />
      </div>

      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('accountModal.username')}
          </label>
          <Input
            value={username}
            dir="ltr"
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('accountModal.nickname')}
          </label>
          <Input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('accountModal.email')}
          </label>
          <Input value={user?.email ?? ''} dir="ltr" disabled className="opacity-60" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('accountModal.bio')}
          </label>
          <Textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder={t('accountModal.bioPlaceholder') as string}
            rows={3}
          />
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-center text-sm text-danger">
          {error}
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} isLoading={saving}>
          {saving ? t('accountModal.saving') : t('accountModal.save')}
        </Button>
      </div>

      {/* Phone — saved on its own, gated by a WhatsApp code sent to the new
          number, so it deliberately sits outside the Save button above. */}
      <div className="mt-6 border-t border-border-default pt-4">
        <label className="mb-1 block text-sm font-medium">
          {t('accountModal.phone')}
        </label>

        {phoneStep === 'idle' && (
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm text-text-secondary">
              <Phone className="h-4 w-4 shrink-0 text-text-muted" />
              {hasPhone ? (
                <bdi dir="ltr">{currentPhone}</bdi>
              ) : (
                <span className="text-text-muted">{t('accountModal.noPhone')}</span>
              )}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPhoneError(null);
                setPhoneCc(user?.phoneCountryCode || '966');
                setPhoneNumber('');
                setPhoneStep('edit');
              }}
            >
              {hasPhone ? t('accountModal.changePhone') : t('accountModal.addPhone')}
            </Button>
          </div>
        )}

        {phoneStep === 'edit' && (
          <div className="space-y-3">
            <p className="text-xs text-text-muted">{t('accountModal.phoneHint')}</p>
            <div className="flex gap-2">
              <Select
                className="w-40 shrink-0"
                value={phoneCc}
                onChange={(e) => setPhoneCc(e.target.value)}
                options={COUNTRY_CODES.map((c) => ({
                  value: c.code,
                  label: `${c.name} +${c.code}`,
                }))}
              />
              <div className="flex-1">
                <Input
                  value={phoneNumber}
                  dir="ltr"
                  type="tel"
                  inputMode="tel"
                  autoFocus
                  placeholder="512345678"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !phoneBusy) handleSendOtp();
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={phoneBusy}
                onClick={() => {
                  setPhoneStep('idle');
                  setPhoneError(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button size="sm" isLoading={phoneBusy} onClick={handleSendOtp}>
                {t('accountModal.sendCode')}
              </Button>
            </div>
          </div>
        )}

        {phoneStep === 'otp' && (
          <div className="space-y-3">
            <p className="text-xs text-text-muted">
              {t('accountModal.otpSentTo')}{' '}
              <bdi dir="ltr">+{phoneCc} {phoneNumber.replace(/\D/g, '')}</bdi>
            </p>
            <Input
              value={otpCode}
              dir="ltr"
              type="text"
              inputMode="numeric"
              maxLength={4}
              autoFocus
              placeholder="••••"
              className="text-center tracking-[0.6em]"
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !phoneBusy) handleVerifyOtp();
              }}
            />
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={phoneBusy}
                onClick={() => {
                  setPhoneStep('edit');
                  setPhoneError(null);
                }}
              >
                {t('accountModal.changeNumber')}
              </Button>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={phoneBusy} onClick={handleSendOtp}>
                  {t('accountModal.resendCode')}
                </Button>
                <Button size="sm" isLoading={phoneBusy} onClick={handleVerifyOtp}>
                  <Check className="h-4 w-4" />
                  {t('accountModal.verify')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {phoneError && (
          <div className="mt-3 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-center text-sm text-danger">
            {phoneError}
          </div>
        )}
      </div>
    </Modal>
  );
}
